import { Typography, makeStyles } from '@material-ui/core'
import { useMutation } from '@apollo/client'
import { pxToRem } from '@deltek/specification-client/core/Utilities/unitUtils'
import {
    DISMISS_SPEC_INTELLIGENCE_FINDING,
    RESOLVE_SPEC_INTELLIGENCE_FINDING,
} from 'api/graphQL/mutations/specIntelligence.mutations'
import type {
    DismissSpecIntelligenceFindingInput,
    DismissSpecIntelligenceFindingResponse,
    FindingResolutionState,
    ResolveSpecIntelligenceFindingInput,
    ResolveSpecIntelligenceFindingResponse,
    SpecIntelligenceFinding,
    SpecIntelligenceReport,
} from 'api/graphQL/types/specIntelligence.types'
import React, { useState } from 'react'
import { compareFindingsBySection, SPEC_INT_NEUTRAL_GRAY_COLOR } from './specIntelligenceConstants'
import SpecIntelligenceFindingCard from './SpecIntelligenceFindingCard'
import SpecIntelligenceStaleBanner from './SpecIntelligenceStaleBanner'
import SpecIntelligenceStatTiles from './SpecIntelligenceStatTiles'

const useStyles = makeStyles(() => ({
    root: {
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        overflow: 'hidden',
    },
    list: {
        flex: 1,
        overflow: 'auto',
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: pxToRem(32),
    },
    emptyText: {
        fontSize: pxToRem(14),
        color: SPEC_INT_NEUTRAL_GRAY_COLOR,
    },
    findingsList: {
        listStyle: 'none',
        padding: 0,
        margin: 0,
    },
}))

interface FindingsListProps {
    findings: SpecIntelligenceFinding[]
    onResolve: (id: string) => void
    onDismiss: (id: string) => void
}

const FindingsList = ({ findings, onResolve, onDismiss }: FindingsListProps) => {
    const classes = useStyles()
    const [expandedId, setExpandedId] = useState<string | null>(null)
    if (findings.length === 0) {
        return (
            <div className={classes.emptyState}>
                <Typography className={classes.emptyText}>No open issues</Typography>
            </div>
        )
    }
    return (
        <ul className={classes.findingsList} aria-label="Findings">
            {findings.map((finding) => (
                <li key={finding.id}>
                    <SpecIntelligenceFindingCard
                        finding={finding}
                        onResolve={onResolve}
                        onDismiss={onDismiss}
                        expanded={expandedId === finding.id}
                        onToggle={() => setExpandedId((prev) => (prev === finding.id ? null : finding.id))}
                    />
                </li>
            ))}
        </ul>
    )
}

/** Props for {@link SpecIntelligenceResultsView}. */
export interface SpecIntelligenceResultsViewProps {
    /** The completed report with all findings. */
    report: SpecIntelligenceReport
    /** Called when the user clicks Re-analyze in the stale banner or equivalent action. */
    onRerun: () => void
    /** Sync a finding's resolution state to the parent hook on optimistic update and rollback. */
    onFindingStateChanged: (id: string, state: FindingResolutionState) => void
}

/**
 * Results view for a completed SpecIntelligence report — shows stat tiles,
 * a list of open findings, and handles resolve/dismiss mutations optimistically.
 */
const SpecIntelligenceResultsView = ({ report, onRerun, onFindingStateChanged }: SpecIntelligenceResultsViewProps) => {
    const classes = useStyles()

    const [openFindings, setOpenFindings] = useState<SpecIntelligenceFinding[]>(() =>
        report.findings
            .filter((f) => f.resolutionState === 'Open')
            .sort(compareFindingsBySection)
    )
    const resolvedCount = report.findings.length - openFindings.length
    const aiCount = openFindings.filter((f) => f.isAI).length

    const [resolveFinding] = useMutation<
        ResolveSpecIntelligenceFindingResponse,
        ResolveSpecIntelligenceFindingInput
    >(RESOLVE_SPEC_INTELLIGENCE_FINDING)

    const [dismissFinding] = useMutation<
        DismissSpecIntelligenceFindingResponse,
        DismissSpecIntelligenceFindingInput
    >(DISMISS_SPEC_INTELLIGENCE_FINDING)

    const removeFinding = (id: string) => {
        setOpenFindings((prev) => prev.filter((f) => f.id !== id))
    }

    const restoreFinding = (finding: SpecIntelligenceFinding) => {
        setOpenFindings((prev) => [...prev, finding])
    }

    const handleResolve = (id: string) => {
        const finding = openFindings.find((f) => f.id === id)
        if (!finding) return
        removeFinding(id)
        onFindingStateChanged(id, 'Resolved')
        resolveFinding({ variables: { input: { findingId: id } } }).catch(() => {
            restoreFinding(finding)
            onFindingStateChanged(id, 'Open')
        })
    }

    const handleDismiss = (id: string) => {
        const finding = openFindings.find((f) => f.id === id)
        if (!finding) return
        removeFinding(id)
        onFindingStateChanged(id, 'Dismissed')
        dismissFinding({ variables: { input: { findingId: id } } }).catch(() => {
            restoreFinding(finding)
            onFindingStateChanged(id, 'Open')
        })
    }

    return (
        <div className={classes.root}>
            <SpecIntelligenceStaleBanner
                projectId={report.projectId}
                sectionNumbers={report.sectionNumbers}
                reportRunDate={report.runDate}
                onRerun={onRerun}
            />
            <SpecIntelligenceStatTiles openCount={openFindings.length} aiCount={aiCount} resolvedCount={resolvedCount} />
            <div className={classes.list}>
                <FindingsList
                    findings={openFindings}
                    onResolve={handleResolve}
                    onDismiss={handleDismiss}
                />
            </div>
        </div>
    )
}

export default SpecIntelligenceResultsView
